import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { PowerLandscapeArtwork } from "@/components/grow/PowerLandscapeArtwork";
import { ELEMENT_CODES, elementImageSrc } from "@/lib/elements";
import type { GameEndSummary as Summary, PowerScore } from "@/lib/games/roleplay/contract";
import { powerName } from "./labels";

// First the tiles fade and rise in, one after another STAGGER_MS apart, each in FADE_MS; once the last one is in, each
// mountain and its number grow from 0 to the score in RISE_MS with smoothstep easing, again STAGGER_MS apart (spark#1236).
const FADE_MS = 1000;
const RISE_MS = 1800;
const STAGGER_MS = 140;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

function smoothstep(fraction: number): number {
  const t = Math.min(1, Math.max(0, fraction));
  return t * t * (3 - 2 * t);
}

/** Milliseconds since the summary appeared, one animation frame at a time until `totalMs`; Infinity without motion. */
function useElapsed(totalMs: number): number {
  const [animate] = useState(() => !prefersReducedMotion());
  const [elapsed, setElapsed] = useState(animate ? 0 : Number.POSITIVE_INFINITY);
  useEffect(() => {
    if (!animate) return;
    let start: number | null = null;
    let frame = 0;
    const tick = (now: number) => {
      start ??= now;
      const passed = now - start;
      setElapsed(passed);
      if (passed < totalMs) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, totalMs]);
  return elapsed;
}

/** A whole number from the game's id, so each game draws its own mountains and a reload draws the same ones. */
function seedOf(gameId: string): number {
  return [...gameId].reduce((hash, letter) => Math.imul(hash, 31) + letter.charCodeAt(0) | 0, 7);
}

/** Powers with a value first, then those without one, each group in the player's order. */
function valuedFirst(scores: readonly PowerScore[]): { power: string; score: number | null }[] {
  const valued = scores.filter(({ score, counted }) => counted && score !== null);
  const unvalued = scores.filter(({ score, counted }) => !(counted && score !== null));
  return [...valued, ...unvalued.map(({ power }) => ({ power, score: null }))];
}

/**
 * The story recap and each chosen Power as a mountain of its score, the Power landscape (spark#1236). The peak's height
 * is the score's; the rest of the shape differs from game to game ([gameId]). Assistive technology reads every Power's
 * final value, never the numbers the animation passes through.
 */
export function GameEndSummary({ summary, gameId }: { summary: Summary; gameId: string }) {
  const intl = useIntl();
  const seed = seedOf(gameId);
  const tiles = valuedFirst(summary.scores);
  // The mountains rise once every tile has faded in; the last to rise is the last Power with a value, the flat ones
  // only fade in.
  const riseStart = FADE_MS + Math.max(0, tiles.length - 1) * STAGGER_MS;
  const valued = tiles.filter(({ score }) => score !== null).length;
  const elapsed = useElapsed(riseStart + RISE_MS + Math.max(0, valued - 1) * STAGGER_MS);
  return <>
    {summary.recap !== null ? <p className="text-smycles-body leading-relaxed text-ink">{summary.recap}</p> : null}
    <ul aria-label={intl.formatMessage({ id: "grow.roleplay.end.scores", defaultMessage: "Your Power scores" })}
      className="grid grid-cols-2 gap-2 @sm:grid-cols-3 @lg:grid-cols-5">
      {tiles.map(({ power, score }, index) => {
        const delay = index * STAGGER_MS;
        const progress = smoothstep((elapsed - riseStart - delay) / RISE_MS);
        const entered = elapsed > 0;
        return <li key={power}
          className={`box-flat relative min-h-44 overflow-hidden bg-surface p-3 text-ink motion-safe:transition-[opacity,transform] motion-safe:duration-1000 motion-safe:ease-out ${entered ? "opacity-100 translate-y-0" : "motion-safe:opacity-0 motion-safe:translate-y-4"}`}
          style={{ transitionDelay: `${delay}ms` }}>
          <div className="relative z-10 flex items-start justify-between gap-2">
            <span lang={intl.locale} className="min-w-0 font-serif text-smycles-body leading-tight hyphens-auto break-words">
              {powerName(intl, power)}
            </span>
            {score !== null ? <>
              {/* The animated number holds the final value's width so the name beside it never rewraps. */}
              <span aria-hidden="true"
                className="grid shrink-0 text-right font-serif text-smycles-title tabular-nums">
                <span className="invisible col-start-1 row-start-1">{score}</span>
                <span className="col-start-1 row-start-1">{Math.round(score * progress)}</span>
              </span>
              <span className="sr-only">{` ${score}`}</span>
            </> : null}
          </div>
          {score === null ? (
            <p className="relative z-10 mt-1 smycles-field-meta text-ink-muted">
              <FormattedMessage id="grow.roleplay.end.notCounted" defaultMessage="Too few clues for a value" />
            </p>
          ) : null}
          <PowerLandscapeArtwork
            code={ELEMENT_CODES[power] ?? power}
            imageSrc={elementImageSrc(power)}
            score={score === null ? null : score * progress}
            seed={seed}
            className="absolute inset-x-0 bottom-0 h-40 w-full text-ink"
          />
        </li>;
      })}
    </ul>
  </>;
}
